import type { RefObject } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { GLYPH_MORE, isAtOrUnder, MORE_LINKS, TAB_LINKS } from './shellNav';

interface MobileTabBarProps {
  isMoreOpen: boolean;
  onOpenMore: () => void;
  moreButtonRef: RefObject<HTMLButtonElement | null>;
}

/**
 * The phone's main navigation: Home, Generate, History, Calendar and More,
 * at the bottom where a thumb reaches. CSS shows it only in the phone tier,
 * where the sidebar's own `nav` is hidden, so exactly one `nav` named "Main"
 * is visible at a time. More reads as current when the page on screen is one
 * of the sheet's links.
 */
export function MobileTabBar({ isMoreOpen, onOpenMore, moreButtonRef }: MobileTabBarProps) {
  const { pathname } = useLocation();
  const isMoreActive = MORE_LINKS.some((item) => isAtOrUnder(pathname, item.to));

  return (
    <nav className="mobile-tabbar" aria-label="Main">
      {TAB_LINKS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            isActive ? 'mobile-tabbar__item mobile-tabbar__item--active' : 'mobile-tabbar__item'
          }
        >
          <span className="mobile-tabbar__icon" aria-hidden="true">
            {String.fromCodePoint(item.icon)}
          </span>
          <span className="mobile-tabbar__label">{item.label}</span>
        </NavLink>
      ))}
      <button
        className={
          isMoreActive ? 'mobile-tabbar__item mobile-tabbar__item--active' : 'mobile-tabbar__item'
        }
        type="button"
        ref={moreButtonRef}
        aria-haspopup="dialog"
        aria-expanded={isMoreOpen}
        onClick={onOpenMore}
      >
        <span className="mobile-tabbar__icon" aria-hidden="true">
          {GLYPH_MORE}
        </span>
        <span className="mobile-tabbar__label">More</span>
        {isMoreActive ? <span className="visually-hidden">, holds the current page</span> : null}
      </button>
    </nav>
  );
}
